import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Eye, EyeOff, Building2, ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { setAuth } from "@/lib/auth";
import { useLang, LangToggle } from "@/components/landing/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CURRICULA = ["International Baccalaureate (IB)", "Cambridge", "ICSE", "CBSE", "State Board", "Others"];
export const DESIGNATIONS = ["Principal/Vice Principal", "Coordinator", "Career Counsellor", "Teacher", "HR Manager/Officer", "Company Owner"];

const UI = {
  en: {
    tag: "For Schools, Colleges & Companies",
    h1: "Institution Portal",
    sub: "Register your institution, share your join code with test takers, and view every student's Bright IQ report from one dashboard.",
    tabRegister: "Register",
    tabLogin: "Sign in",
    instName: "School / College / Company Name *",
    curriculum: "Curriculum *",
    address: "Address *",
    officialName: "Name of the Official *",
    designation: "Designation *",
    email: "Official Email ID *",
    mobile: "Mobile Number *",
    password: "Password (min 6 characters) *",
    registerBtn: "Register institution",
    loginBtn: "Sign in to portal",
    otpTitle: "Verify your email",
    otpSub: (email) => `We sent a 6-digit code to ${email}. It expires in 10 minutes.`,
    otpPlaceholder: "6-digit code from email",
    otpBtn: "Verify & submit registration",
    otpBack: "← Edit details",
    resend: "Resend code",
    resendIn: (s) => `Resend in ${s}s`,
    codeSent: "Verification code sent to your email",
    newCodeSent: "New code sent",
    pendingTitle: "Registration submitted",
    pendingSub: "Your institution account is awaiting approval from the Bright IQ team. We will email your student join code as soon as it is approved.",
    pendingNote: "You can sign in once approved.",
    goLogin: "Go to sign in",
    approvedNote: "Approved institutions sign in here. New here? Register first — approval usually takes under 24 hours.",
    networkErr: "Network error. Please try again.",
    welcomeToast: (name) => `Welcome, ${name}`,
    selectPlaceholder: "Select",
  },
  hi: {
    tag: "स्कूल, कॉलेज और कंपनियों के लिए",
    h1: "संस्थान पोर्टल",
    sub: "अपना संस्थान रजिस्टर करें, टेस्ट देने वालों के साथ अपना जॉइन कोड साझा करें, और एक डैशबोर्ड से हर विद्यार्थी की Bright IQ रिपोर्ट देखें।",
    tabRegister: "रजिस्टर करें",
    tabLogin: "साइन इन",
    instName: "स्कूल / कॉलेज / कंपनी का नाम *",
    curriculum: "पाठ्यक्रम (करिकुलम) *",
    address: "पता *",
    officialName: "अधिकारी का नाम *",
    designation: "पद *",
    email: "आधिकारिक ईमेल *",
    mobile: "मोबाइल नंबर *",
    password: "पासवर्ड (कम से कम 6 अक्षर) *",
    registerBtn: "संस्थान रजिस्टर करें",
    loginBtn: "पोर्टल में साइन इन करें",
    otpTitle: "अपना ईमेल सत्यापित करें",
    otpSub: (email) => `हमने ${email} पर 6-अंकीय कोड भेजा है। यह 10 मिनट में समाप्त होगा।`,
    otpPlaceholder: "ईमेल से 6-अंकीय कोड",
    otpBtn: "सत्यापित करें और रजिस्ट्रेशन भेजें",
    otpBack: "← विवरण बदलें",
    resend: "कोड फिर भेजें",
    resendIn: (s) => `${s}s में फिर भेजें`,
    codeSent: "सत्यापन कोड आपके ईमेल पर भेजा गया",
    newCodeSent: "नया कोड भेजा गया",
    pendingTitle: "रजिस्ट्रेशन जमा हुआ",
    pendingSub: "आपका संस्थान खाता Bright IQ टीम की मंज़ूरी की प्रतीक्षा में है। मंज़ूरी मिलते ही हम आपका स्टूडेंट जॉइन कोड ईमेल करेंगे।",
    pendingNote: "मंज़ूरी के बाद आप साइन इन कर सकेंगे।",
    goLogin: "साइन इन पर जाएँ",
    approvedNote: "मंज़ूर संस्थान यहाँ साइन इन करें। नए हैं? पहले रजिस्टर करें — मंज़ूरी आमतौर पर 24 घंटे के अंदर हो जाती है।",
    networkErr: "नेटवर्क त्रुटि। कृपया फिर प्रयास करें।",
    welcomeToast: (name) => `स्वागत है, ${name}`,
    selectPlaceholder: "चुनें",
  },
};

const inputCls = "w-full rounded-2xl border border-ink/15 bg-cream px-5 py-3.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const labelCls = "block text-xs font-bold tracking-wide text-ink/60 uppercase mb-1.5";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function InstitutionAuth({ onSuccess }) {
  const { lang } = useLang();
  const ui = UI[lang] || UI.en;
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", curriculum: "", address: "", official_name: "", designation: "", email: "", mobile: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState(null); // { signupId, email }
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [done, setDone] = useState(false);
  const [loginErr, setLoginErr] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const iv = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(iv);
  }, [cooldown]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resendOtp = async () => {
    try {
      const res = await fetch(`${API}/institution/register/resend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: otp.signupId }),
      });
      const data = await res.json();
      if (!res.ok) toast.error(fmtErr(data.detail));
      else { toast.success(ui.newCodeSent); setCooldown(60); }
    } catch { toast.error(ui.networkErr); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/institution/register/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: otp.signupId, email_otp: code }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(fmtErr(data.detail));
        setBusy(false);
        return;
      }
      setOtp(null);
      setDone(true);
      setBusy(false);
    } catch {
      toast.error(ui.networkErr);
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginErr("");
    try {
      const isLogin = mode === "login";
      const res = await fetch(`${API}/institution/${isLogin ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isLogin
            ? { email: form.email, password: form.password }
            : { name: form.name, curriculum: form.curriculum, address: form.address, official_name: form.official_name, designation: form.designation, email: form.email, mobile: form.mobile, password: form.password }
        ),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = fmtErr(data.detail);
        if (isLogin) setLoginErr(msg); else toast.error(msg);
        setBusy(false);
        return;
      }
      if (!isLogin && data.otpRequired) {
        setOtp({ signupId: data.signupId, email: data.email });
        setCode("");
        setCooldown(60);
        toast.success(ui.codeSent);
        setBusy(false);
        return;
      }
      if (!isLogin) {
        setDone(true);
        setBusy(false);
        return;
      }
      setAuth(data.token, data.user);
      try { localStorage.setItem("biq_institution", JSON.stringify(data.institution)); } catch {}
      toast.success(ui.welcomeToast(data.institution?.name || data.user?.name || ""));
      setBusy(false);
      onSuccess?.(data.user);
    } catch {
      toast.error(ui.networkErr);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="institution-auth-page">
      <header className="max-w-6xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="inst-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span> <span className="text-ink/35 font-body text-xs font-semibold tracking-[0.2em] uppercase ml-1">{ui.tag}</span></span>
        </Link>
        <LangToggle />
      </header>

      <main className="max-w-6xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="pt-6">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-iqblue/10 items-center justify-center"><Building2 className="h-6 w-6 text-iqblue" /></span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-6">{ui.h1}</h1>
          <p className="mt-4 text-ink/60 text-sm sm:text-base max-w-md leading-relaxed">{ui.sub}</p>
          <div className="mt-8 flex items-start gap-3 rounded-2xl bg-iqgreen/10 border border-iqgreen/25 px-5 py-4 max-w-md">
            <ShieldCheck className="h-5 w-5 text-iqgreen shrink-0 mt-0.5" />
            <p className="text-sm text-ink/70 leading-relaxed">{ui.approvedNote}</p>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="rounded-[2rem] bg-cream border border-ink/10 p-8">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6" data-testid="inst-pending-screen">
                <span className="inline-flex h-14 w-14 rounded-2xl bg-iqamber/15 items-center justify-center"><MailCheck className="h-6 w-6 text-iqamber" /></span>
                <h3 className="font-display font-bold text-2xl tracking-tight mt-5">{ui.pendingTitle}</h3>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed">{ui.pendingSub}</p>
                <p className="mt-2 text-xs font-semibold text-ink/45">{ui.pendingNote}</p>
                <button onClick={() => { setDone(false); setMode("login"); }} data-testid="inst-go-login-button" className="mt-7 bg-ink text-paper font-semibold px-8 py-3.5 rounded-full hover:bg-iqblue transition-colors duration-300">
                  {ui.goLogin}
                </button>
              </motion.div>
            ) : otp ? (
              <motion.div key="otp" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <h3 className="font-display font-bold text-2xl tracking-tight">{ui.otpTitle}</h3>
                <p className="mt-2 text-sm text-ink/60">{ui.otpSub(otp.email)}</p>
                <form onSubmit={verifyOtp} className="mt-6 space-y-3" data-testid="inst-otp-step">
                  <input data-testid="inst-otp-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder={ui.otpPlaceholder} required className={`${inputCls} text-center text-xl tracking-[0.5em] font-bold`} />
                  <button type="submit" disabled={busy} data-testid="inst-otp-verify-button" className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : ui.otpBtn}
                  </button>
                  <div className="flex items-center justify-between text-xs font-semibold pt-1">
                    <button type="button" onClick={() => setOtp(null)} data-testid="inst-otp-back-button" className="text-ink/50 hover:text-ink transition-colors">{ui.otpBack}</button>
                    <button type="button" disabled={cooldown > 0} onClick={resendOtp} data-testid="inst-otp-resend" className="text-iqblue hover:underline disabled:text-ink/30 disabled:no-underline">
                      {cooldown > 0 ? ui.resendIn(cooldown) : ui.resend}
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div key={mode} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <div className="grid grid-cols-2 rounded-full border border-ink/15 p-1">
                  {[["register", ui.tabRegister], ["login", ui.tabLogin]].map(([m, label]) => (
                    <button key={m} type="button" data-testid={`inst-tab-${m}`} onClick={() => { setMode(m); setLoginErr(""); }} className={`py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${mode === m ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}>
                      {label}
                    </button>
                  ))}
                </div>

                <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid={mode === "login" ? "inst-login-form" : "inst-register-form"}>
                  {mode === "register" && (
                    <>
                      <div>
                        <label className={labelCls}>{ui.instName}</label>
                        <input data-testid="inst-name-input" value={form.name} onChange={set("name")} required className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>{ui.curriculum}</label>
                        <select data-testid="inst-curriculum-select" value={form.curriculum} onChange={set("curriculum")} required className={`${inputCls} ${form.curriculum ? "text-ink/80" : "text-ink/35"}`}>
                          <option value="" disabled>{ui.selectPlaceholder}</option>
                          {CURRICULA.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{ui.address}</label>
                        <textarea data-testid="inst-address-input" value={form.address} onChange={set("address")} required minLength={5} rows={2} className={`${inputCls} resize-none`} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>{ui.officialName}</label>
                          <input data-testid="inst-official-name-input" value={form.official_name} onChange={set("official_name")} required className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>{ui.designation}</label>
                          <select data-testid="inst-designation-select" value={form.designation} onChange={set("designation")} required className={`${inputCls} ${form.designation ? "text-ink/80" : "text-ink/35"}`}>
                            <option value="" disabled>{ui.selectPlaceholder}</option>
                            {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    {mode === "register" && <label className={labelCls}>{ui.email}</label>}
                    <input data-testid="inst-email-input" type="email" value={form.email} onChange={set("email")} placeholder={mode === "login" ? ui.email : ""} required className={inputCls} />
                  </div>
                  {mode === "register" && (
                    <div>
                      <label className={labelCls}>{ui.mobile}</label>
                      <input data-testid="inst-mobile-input" type="tel" value={form.mobile} onChange={set("mobile")} required className={inputCls} />
                    </div>
                  )}
                  <div>
                    {mode === "register" && <label className={labelCls}>{ui.password}</label>}
                    <div className="relative">
                      <input data-testid="inst-password-input" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder={mode === "login" ? ui.password : ""} required minLength={6} className={`${inputCls} pr-12`} />
                      <button type="button" onClick={() => setShowPw(!showPw)} data-testid="inst-password-toggle" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors" aria-label="Toggle password visibility">
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {loginErr && mode === "login" && (
                    <div className="rounded-2xl bg-iqamber/10 border border-iqamber/30 px-5 py-3.5 text-sm text-ink/75" data-testid="inst-login-error">{loginErr}</div>
                  )}
                  <button type="submit" disabled={busy} data-testid={mode === "login" ? "inst-login-submit" : "inst-register-submit"} className="w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? ui.loginBtn : ui.registerBtn}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
}
